<template>
  <v-dialog :model-value="modelValue" max-width="1100" persistent @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title style="font-size: 16px">{{ $t('graphRagStructuredImport') }}</v-card-title>
      <v-card-text>
        <v-tabs v-model="step" density="compact" class="mb-4">
          <v-tab :value="0" data-testid="wizard-step-preview">1. {{ $t('graphRagStepUpload') }}</v-tab>
          <v-tab :value="1" data-testid="wizard-step-mapping" :disabled="!preview">2. {{ $t('graphRagStepMapping') }}</v-tab>
          <v-tab :value="2" data-testid="wizard-step-diff" :disabled="!diff">3. {{ $t('graphRagStepPreview') }}</v-tab>
        </v-tabs>

        <!-- 1. ดูตาราง -->
        <div v-if="step === 0">
          <!-- OBS-1 — โหมดตารางรับแค่ CSV/XLSX ต้องบอกให้ตรง ไม่ใช่ลิสต์ชนิดไฟล์ของการนำเข้าเอกสาร -->
          <div class="mb-3" style="font-size: 12px; color: #646464">{{ $t('graphRagWizTabularSupported') }}</div>

          <v-select
            v-if="preview && preview.sheetNames.length > 1"
            v-model="sheetName"
            data-testid="wizard-sheet"
            :items="preview.sheetNames"
            label="Sheet"
            density="compact"
            variant="outlined"
            class="mb-3"
            @update:model-value="loadPreview"
          />

          <v-btn v-if="!preview" color="#C32E0E" data-testid="wizard-load" :loading="loading" @click="loadPreview">
            {{ $t('graphRagStepUpload') }}
          </v-btn>

          <template v-if="preview">
            <div class="mb-2" style="font-size: 13px">
              {{ preview.sheetName }} · {{ preview.totalRows }} rows
            </div>

            <v-table density="compact" data-testid="wizard-sample">
              <thead>
                <tr><th v-for="h in preview.headers" :key="h">{{ h }}</th></tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in preview.sampleRows" :key="i">
                  <td v-for="(cell, j) in row" :key="j">{{ cell }}</td>
                </tr>
              </tbody>
            </v-table>

            <div class="d-flex justify-end mt-3">
              <v-btn color="#C32E0E" data-testid="wizard-to-mapping" :loading="loading" @click="loadMapping">
                {{ $t('graphRagStepMapping') }}
              </v-btn>
            </div>
          </template>
        </div>

        <!-- 2. จับคู่คอลัมน์ -->
        <div v-else-if="step === 1">
          <v-row>
            <v-col cols="12" md="6">
              <v-select v-model="mapping.nodeKey" data-testid="wizard-map-node-key" :items="columnNames" :label="$t('graphRagNodeKey')" density="compact" variant="outlined" />
              <v-select v-model="mapping.label" data-testid="wizard-map-label" :items="columnNames" :label="$t('graphRagRoleName')" density="compact" variant="outlined" clearable />
              <v-select v-model="mapping.anchor" data-testid="wizard-map-anchor" :items="columnNames" :label="$t('graphRagRoleAnchor')" density="compact" variant="outlined" clearable />
              <v-select v-model="mapping.description" data-testid="wizard-map-description" :items="columnNames" :label="$t('graphRagRoleDescription')" density="compact" variant="outlined" clearable />
              <v-text-field v-model="mapping.nodeTypeConstant" data-testid="wizard-map-node-type" :label="$t('graphRagNodeType')" density="compact" variant="outlined" />
            </v-col>

            <v-col cols="12" md="6">
              <v-select v-model="matchStrategy" data-testid="wizard-match-strategy" :items="matchStrategies" item-title="text" item-value="value" :label="$t('graphRagMatchStrategy')" density="compact" variant="outlined" />
              <v-select v-model="onMissing" data-testid="wizard-on-missing" :items="onMissingOptions" item-title="text" item-value="value" :label="$t('graphRagOnMissing')" density="compact" variant="outlined" />

              <div class="mt-3" style="font-size: 13px; font-weight: 600">{{ $t('graphRagSuggested') }}</div>
              <v-table density="compact" data-testid="wizard-suggestions">
                <thead>
                  <tr>
                    <th>{{ $t('graphRagColumn') }}</th>
                    <th>{{ $t('graphRagRole') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in suggestions" :key="c.column">
                    <td>{{ c.column }}</td>
                    <td>
                      <!-- ไม่มั่นใจพอ = บอกตรง ๆ ไม่ใช่แนะนำมั่ว -->
                      <span v-if="c.suggestedRole">{{ roleLabel(c.suggestedRole) }} ({{ c.confidenceLabel }})</span>
                      <span v-else style="color: #646464">{{ $t('graphRagNoSuggestion') }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-3">
            <v-btn color="#C32E0E" data-testid="wizard-to-diff" :loading="loading" :disabled="!mapping.nodeKey" @click="mapping.nodeKey && loadDiff()">
              {{ $t('graphRagStepPreview') }}
            </v-btn>
          </div>
        </div>

        <!-- 3. ดูผลก่อนนำเข้า -->
        <div v-else-if="step === 2 && diff">
          <div class="d-flex flex-wrap mb-3" style="gap: 12px">
            <v-chip size="small" color="success">{{ $t('graphRagDiffAdded') }} {{ diff.addedCount }}</v-chip>
            <v-chip size="small" color="#C32E0E">{{ $t('graphRagDiffUpdated') }} {{ diff.updatedCount }}</v-chip>
            <v-chip size="small">{{ $t('graphRagDiffUnchanged') }} {{ diff.unchangedCount }}</v-chip>
            <v-chip size="small" color="warning">{{ $t('graphRagDiffRemoved') }} {{ diff.removedCount }}</v-chip>
            <v-chip v-if="diff.ambiguousCount" size="small" color="error">
              {{ $t('graphRagDiffAmbiguous') }} {{ diff.ambiguousCount }}
            </v-chip>
          </div>

          <v-alert v-if="diff.ambiguousCount" type="error" variant="tonal" density="compact" class="mb-3">
            {{ $t('graphRagAmbiguousBlocks') }}
          </v-alert>

          <ul v-if="diff.warnings.length" style="font-size: 12px; color: #646464" class="mb-3">
            <li v-for="(w, i) in diff.warnings" :key="i">{{ w }}</li>
          </ul>

          <v-data-table :items="diff.rows" :headers="diffHeaders" density="compact" :items-per-page="10" data-testid="wizard-diff-table" />
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" data-testid="wizard-close" @click="$emit('update:modelValue', false)">{{ $t('close') }}</v-btn>
        <!-- โหมด configureOnly (เรียกจาก Import Wizard ขั้น 4) คืนค่าการจับคู่ให้ผู้เรียกแทนการนำเข้าทันที -->
        <v-btn
          v-if="step === 2"
          color="#C32E0E"
          data-testid="wizard-execute"
          :loading="executing"
          :disabled="!canEdit || (diff && diff.ambiguousCount > 0)"
          @click="canEdit && diff && diff.ambiguousCount === 0 && confirmStep()"
        >
          {{ configureOnly ? $t('graphRagWizUseMapping') : $t('graphRagApplyAll') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'StructuredImportWizard',
  props: {
    modelValue: { type: Boolean, default: false },
    graphKey: { type: String, required: true },
    sessionId: { type: String, default: null },
    canEdit: { type: Boolean, default: false },
    /** ชีตตั้งต้นเมื่อถูกเปิดจาก Import Wizard — ว่าง = ใช้ชีตแรกที่ backend คืนมาเหมือนเดิม */
    initialSheet: { type: String, default: null },
    /**
     * true = เปิดมาเพื่อ "ตั้งค่าคอลัมน์" ให้ Import Wizard เท่านั้น ปุ่มท้ายจะคืนค่าที่จับคู่ไว้ทาง configured
     * แล้วให้ผู้เรียกเป็นคนยิง GraphRagImportExecute ตอนกดสร้างที่ขั้น 5
     * ค่าเริ่มต้น false ⇒ เส้นทางเดิมที่เรียกจาก IngestPanel ทำงานเหมือนเดิมทุกประการ (TC-47)
     */
    configureOnly: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'notify', 'imported', 'configured'],
  data() {
    return {
      step: 0,
      loading: false,
      executing: false,
      preview: null,
      suggestions: [],
      diff: null,
      sheetName: null,
      matchStrategy: 'by-node-key',
      onMissing: 'keep',
      mapping: { nodeKey: null, label: null, anchor: null, description: null, nodeTypeConstant: 'Product', attributes: {} },
    };
  },
  computed: {
    columnNames() {
      return this.preview ? this.preview.headers : [];
    },
    matchStrategies() {
      return [
        { text: this.$t('graphRagNodeKey'), value: 'by-node-key' },
        { text: this.$t('graphRagLabel'), value: 'by-label' },
        { text: this.$t('graphRagRoleIgnore'), value: 'none' },
      ];
    },
    onMissingOptions() {
      return [
        { text: this.$t('graphRagOnMissingKeep'), value: 'keep' },
        { text: this.$t('graphRagOnMissingDeactivate'), value: 'deactivate' },
      ];
    },
    diffHeaders() {
      return [
        { title: this.$t('graphRagNodeKey'), value: 'nodeKey', sortable: false },
        { title: this.$t('graphRagLabel'), value: 'label', sortable: false },
        { title: this.$t('graphRagRole'), value: 'change', sortable: false },
        { title: 'rule', value: 'matchRule', sortable: false },
      ];
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.step = 0;
        this.preview = null;
        this.diff = null;
        // เปิดจาก Import Wizard = รู้อยู่แล้วว่าจะตั้งค่าชีตไหน จึงเริ่มอ่านตารางให้เลย ไม่ต้องให้กดซ้ำ
        this.sheetName = this.initialSheet || null;
        if (this.configureOnly && this.sessionId) this.loadPreview();
      }
    },
  },
  methods: {
    notify(text, colour) {
      this.$emit('notify', { text, colour: colour || 'success' });
    },
    roleLabel(role) {
      const map = {
        name: this.$t('graphRagRoleName'),
        anchor: this.$t('graphRagRoleAnchor'),
        description: this.$t('graphRagRoleDescription'),
        ignore: this.$t('graphRagRoleIgnore'),
      };

      return map[role] || role;
    },
    request() {
      return { graphKey: this.graphKey, sessionId: this.sessionId, sheetName: this.sheetName };
    },
    loadPreview() {
      this.loading = true;

      this.$call.GraphRagStructuredPreview(
        this.request(),
        (res) => {
          this.loading = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.preview = res.data;
          this.sheetName = res.data.sheetName;
        },
        () => {
          this.loading = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    loadMapping() {
      this.loading = true;

      this.$call.GraphRagSuggestMapping(
        this.request(),
        (res) => {
          this.loading = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.suggestions = res.data.columns || [];
          this.mapping.nodeKey = res.data.suggestedNodeKey;
          this.mapping.label = res.data.suggestedLabel;
          this.mapping.anchor = res.data.suggestedAnchor;
          this.mapping.description = res.data.suggestedDescription;
          this.step = 1;
        },
        () => {
          this.loading = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    importRequest() {
      return {
        graphKey: this.graphKey,
        sessionId: this.sessionId,
        sheetName: this.sheetName,
        mapping: this.mapping,
        matchStrategy: this.matchStrategy,
        onMissing: this.onMissing,
      };
    },
    loadDiff() {
      this.loading = true;

      this.$call.GraphRagImportDiff(
        this.importRequest(),
        (res) => {
          this.loading = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.diff = res.data;
          this.step = 2;
        },
        () => {
          this.loading = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    /** ปุ่มท้ายเดินคนละเส้นตามโหมด — ตั้งค่าให้ผู้เรียก หรือ นำเข้าทันทีแบบเดิม */
    confirmStep() {
      if (!this.configureOnly) return this.execute();

      this.$emit('configured', {
        sheetName: this.sheetName,
        mapping: JSON.parse(JSON.stringify(this.mapping)),
        matchStrategy: this.matchStrategy,
        onMissing: this.onMissing,
      });

      // ปิดตัวเองด้วย ไม่รอผู้เรียกสั่ง — ผู้เรียกเป็น component ที่ re-render ทั้งก้อนตอนรับค่า
      return this.$emit('update:modelValue', false);
    },
    execute() {
      this.executing = true;

      this.$call.GraphRagImportExecute(
        this.importRequest(),
        (res) => {
          this.executing = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.notify(this.$t('graphRagSaved'), 'success');
          this.$emit('imported');
          this.$emit('update:modelValue', false);
        },
        () => {
          this.executing = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
  },
};
</script>
